import React, { useState, useEffect } from 'react';
import AvatarUserStatus from 'components/shareable/AvatarUserStatus';
import FreePlanView from './FreePlanView';
import PaidPlanBillingView from './PaidPlanBillingView/PaidPlanBillingView';
import { OrganizationData, OrganizationStatus } from 'modules/Types';
import CurrencyDropdown from '../../../components/shareable/CurrencyDropdown';
import { CURRENCY_SYMBOLS, DEFAULT_CURRENCY } from 'modules/Const';
import OrganizationProPlanView from './PaidPlanBillingView/organization';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import { Container, StyledLabelP } from 'modules/Sty';
import InvoiceList from './InvoiceList';
import { getBrandInvoices_api, Invoice as ApiInvoice } from 'api/brandApi';

interface BillingTabProps {
  organization?: OrganizationData;
  brandBelongsToOrganization?: boolean;
}

/** Invoice data structure for component state */
interface ComponentInvoice {
  id: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  status: string;
  dueDate: string;
  createdDate: string;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  payment_description?: string;
  payment_name?: string;
}

const BillingTab = ({
  organization,
  brandBelongsToOrganization
}: BillingTabProps) => {
  const { isPaidBrand, currentBrand } = useBrand() as BrandExistsState;
  const [invoices, setInvoices] = useState<ComponentInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  useEffect(() => {
    const loadInvoices = async () => {
      if (!currentBrand?.id) {
        setIsLoadingInvoices(false);
        return;
      }

      setIsLoadingInvoices(true);
      setInvoiceError(null);

      try {
        const apiInvoices: ApiInvoice[] = await getBrandInvoices_api(currentBrand.id.toString());

        const formattedInvoices: ComponentInvoice[] = apiInvoices.map(inv => {
          // Handle due_date as string timestamp or null
          let dueDate = 'N/A';
          if (inv.due_date) {
            const timestamp = parseInt(inv.due_date, 10);
            if (!isNaN(timestamp)) {
              dueDate = new Date(timestamp * 1000).toLocaleDateString();
            }
          }

          return {
            id: inv.id,
            amountDue: inv.amount_due / 100, // Convert from cents
            amountPaid: inv.amount_paid / 100,
            amountRemaining: inv.amount_remaining / 100,
            currency: inv.currency.toUpperCase(),
            status: inv.status,
            dueDate,
            createdDate: new Date(inv.created * 1000).toLocaleDateString(),
            invoice_pdf: inv.invoice_pdf,
            hosted_invoice_url: inv.hosted_invoice_url,
            payment_description: inv.payment_description,
            payment_name: inv.payment_name,
          };
        });

        setInvoices(formattedInvoices);
      } catch (error) {
        console.error('Failed to fetch invoices:', error);
        setInvoiceError('Failed to load billing history. Please try again later.');
      } finally {
        setIsLoadingInvoices(false);
      }
    };

    loadInvoices();
  }, [currentBrand?.id]);

  const { currency } = currentBrand;

  const renderPrimaryView = () => {
    // Helper function to render Pro/Free view based on user status
    const renderPlanView = () => {
      if (isPaidBrand) {
        return <PaidPlanBillingView />;
      }
      return <FreePlanView />;
    };

    // If organization is ready, check if brand belongs to org
    if (organization?.setup_status === OrganizationStatus.READY) {
      // Show org view if brand belongs to org, otherwise show plan view
      if (brandBelongsToOrganization) {
        return (
          <OrganizationProPlanView
            organization={organization}
          />
        );
      }

      return renderPlanView();
    }

    // Default to plan view if no org or org not ready
    return renderPlanView();
  };

  const hasInvoices = invoices.length > 0;
  const renderBillingHistory = () => {
    if (isLoadingInvoices) {
      return <StyledLabelP>Loading billing history...</StyledLabelP>;
    }

    if (invoiceError) {
      return <StyledLabelP color="error">{invoiceError}</StyledLabelP>;
    }

    if (hasInvoices) {
      return <InvoiceList invoices={invoices} />;
    }

    return null;
  };

  return (
    <Container className="billingTab">
      <Container
        className="billingHeader"
        padding="0 0 24 0"
        borderBottom="1px solid #E5E7EB"
      >
        <StyledLabelP fontSize={20}>Billing</StyledLabelP>
      </Container>

      <Container
        className="userStatusContainer"
        padding="24 0"
        borderBottom="1px solid #E5E7EB"
      >
        <AvatarUserStatus
          className="avatarUserStatusBillingTab"
          isWhiteBorder
          userContainerStyles={{ gap: 4 }}
          styles={{ gap: 16 }}
          userNameTextStyles={{ fontSize: 18 }}
          proTextStyles={{ fontSize: 12, color: '#E08507' }}
        />
      </Container>

      <CurrencyDropdown
        initialCurrency={currency?.code
          ? `${currency.code} (${CURRENCY_SYMBOLS[currency.code]})`
          : `${DEFAULT_CURRENCY} (${CURRENCY_SYMBOLS[DEFAULT_CURRENCY]})`
        }
      />

      {renderPrimaryView()}

      {hasInvoices && <Container
        className="billingHistorySection"
        margin="32 0 0 0"
        padding="24 0"
        style={{ borderTop: '1px solid #E5E7EB' }}
        borderBottom="1px solid #E5E7EB"
      >
        <StyledLabelP
          className="billingHistoryTitle"
          fontSize={20}
          margin="0 0 16 0"
        >
          Billing History
        </StyledLabelP>
        {renderBillingHistory()}
      </Container>}
    </Container>
  );
};

export default BillingTab;
